import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpHeaders,
  HttpEventType,
  HttpUploadProgressEvent
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, from, EMPTY } from 'rxjs';
import { catchError, finalize, tap, filter, take, switchMap, mergeMap, timeout, takeUntil } from "rxjs/operators";
import * as CryptoJS from "crypto-js";
import { Router, NavigationStart } from '@angular/router';
import { HelperService } from '../helper/helper.service';
import { NotificationService } from '../services/notification.service';
import { AuthService } from 'src/app/modules/auth/auth.service';
import { CookieService } from 'ngx-cookie-service';
import { CommonService } from '../services/common.service';
import { NetworkStatusService } from '../services/network-status.service';

@Injectable()
export class CommonInterceptor implements HttpInterceptor {

  public message: any;
  public nonce: any = "35A90B73C39D97";
  public path: any;
  public privateKey: any = "4AF8AC8E124B6484BDDC5A9EB3D49";
  public secretKey: any = "PXGuKjNtjzLiyLRE0GzykvGcaZO5uWE0";
  public timestamp: any = 1615971782;
  public accounttype!: any;
  private activeRequests = 0;
  private unauthorized: boolean = false;

  private networkErrorShown = false;
  private networkErrorResetTimer: any = null;

  // Token refresh mechanism
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  private readonly network = inject(NetworkStatusService);
  readonly isOnline = this.network.online;

  constructor(
    private commonService: CommonService,
    private router: Router,
    private helper: HelperService,
    private notifyService: NotificationService,
    private authService: AuthService,
    private cookieService: CookieService
  ) { }

  intercept(httpRequest: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Check internet connection
    if (!this.isOnline()) {
      return throwError(() => new Error('No internet connection'));
    }

    // Wait for auth initialization, then proceed with request
    return from(this.authService.authInitialized()).pipe(
      mergeMap(() => {
        // Start loader
        const skipLoader = this.shouldSkipLoader(httpRequest.url);
        if (!skipLoader) {
          this.commonService.updateloadingState(true);
          this.activeRequests++;
        }

        // Detect whether this request carries actual file/blob attachments.
        const hasFiles = httpRequest.body instanceof FormData
          && this.hasFileAttachments(httpRequest.body as FormData);

        // Add authentication headers
        let authReq = this.addAuthHeaders(httpRequest);

        // Enable per-byte upload progress events for file-carrying requests.
        if (hasFiles) {
          authReq = authReq.clone({ reportProgress: true });
        }

        // Guest "quote" flow uses its own non-refreshable token (quote_token) and
        // never goes through the customer session/refresh-token flow.
        const isQuoteFlow = !!localStorage.getItem("quote_token");
        const isExcludedEndpoint = isQuoteFlow
          || authReq.url.includes('/refresh-token')
          || authReq.url.includes('/logout')
          || this.isPublicEndpoint(authReq.url);

        let responseObservable: Observable<HttpEvent<any>>;
        // Check if the access token is already mathematically expired or the session cookie is completely missing before making an API call
        const currentToken = this.authService.getToken();
        const hasSessionCookie = this.cookieService.check("customer_user");

        if (!isExcludedEndpoint && ((currentToken && this.isTokenExpired(currentToken)) || !hasSessionCookie)) {
          // Skip API call entirely, directly trigger refresh & logout flow
          responseObservable = this.handleTokenRefresh(authReq, next);
        } else {
          responseObservable = next.handle(authReq).pipe(
            tap((event: HttpEvent<any>) => {
              // Track byte-level upload progress so the UI can show a % indicator
              if (hasFiles && event.type === HttpEventType.UploadProgress) {
                const e = event as HttpUploadProgressEvent;
                if (e.total) {
                  this.commonService.updateUploadProgress(Math.round((e.loaded / e.total) * 100));
                }
              }

              if (event instanceof HttpResponse && !isExcludedEndpoint) {
                // code 401 → unauthorized session (account inactive, wrong session, etc.)
                // Logout immediately – no refresh attempt.
                if (event.body && event.body.code === 401) {
                  if (!this.unauthorized) {
                    if (!this.helper.isLoggingOutNow()) {
                      this.notifyService.showError("Unauthorized or Session Expired! Sign In again...", "");
                    }
                    this.unauthorized = true;
                  }
                  this.helper.Logout();
                  throw event; // stop further processing
                }
                // code 403 → access token expired → trigger silent refresh flow
                if (event.body && event.body.code === 403) {
                  throw event; // caught by catchError below
                }
              }
            }),
            catchError((error) => {
              // Access token expired → attempt silent token refresh
              if (error instanceof HttpResponse && error.body?.code === 403 && !isExcludedEndpoint) {
                return this.handleTokenRefresh(authReq, next);
              }

              this.handleError(httpRequest, error);
              return throwError(() => error);
            })
          );
        }

        // File uploads: timeout resets on every UploadProgressEvent emission.
        // Regular requests: 2-minute total timeout as a universal safety net.
        const requestTimeout = hasFiles
          ? timeout<HttpEvent<any>>({ each: 5 * 60_000 })
          : timeout<HttpEvent<any>>(2 * 60_000);

        // When the user navigates to another route, cancel the in-flight request
        // so the overlay doesn't bleed onto the new page.
        const navigationCancel$ = this.router.events.pipe(
          filter(e => e instanceof NavigationStart)
        );

        return responseObservable.pipe(
          requestTimeout,
          takeUntil(navigationCancel$),
          catchError((err) => {
            if (err.name === 'TimeoutError') {
              const msg = hasFiles
                ? 'Upload timed out. The connection may be too slow or the server is not responding. Please try again.'
                : 'Request timed out. Please try again.';
              this.notifyService.showError(msg, '');
              // EMPTY completes cleanly so finalize() runs and the loader clears.
              return EMPTY;
            }
            return throwError(() => err);
          }),
          finalize(() => {
            if (hasFiles) {
              this.commonService.updateUploadProgress(null);
            }
            if (!skipLoader) {
              this.activeRequests = Math.max(0, this.activeRequests - 1);

              if (this.activeRequests === 0) {
                this.unauthorized = false;
                this.commonService.updateloadingState(false);
              }
            }
          })
        );
      })
    );
  }

  private addAuthHeaders(httpRequest: HttpRequest<unknown>): HttpRequest<unknown> {
    // Generate Nonce
    const chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXTZabcdefghiklmnopqrstuvwxyz";
    this.nonce = "";
    for (let i = 0; i < 8; i++) {
      const rnum = Math.floor(Math.random() * chars.length);
      this.nonce += chars.substring(rnum, rnum + 1);
    }

    // Generate TimeStamp
    this.timestamp = Math.floor(new Date().getTime() / 1000);

    const quoteToken = localStorage.getItem("quote_token");
    const tempacctype = quoteToken ? '2' : localStorage.getItem("customer_account_type");
    this.accounttype = this.helper.staticPush(tempacctype);

    // Generate HMAC token
    const generateHmacToken = CryptoJS.HmacSHA256(
      this.secretKey + this.timestamp + this.nonce,
      this.privateKey
    ).toString(CryptoJS.enc.Hex);

    const token = quoteToken ? quoteToken : this.authService.getToken();
    let headers = new HttpHeaders()
      .set("nonce", this.nonce.toString())
      .set("timestamp", this.timestamp.toString())
      .set("token", generateHmacToken.toString());

    if (token) {
      headers = headers.set("authorization", token).set("accounttype", this.accounttype);
    }

    return httpRequest.clone({
      withCredentials: false,
      headers: headers
    });
  }

  private handleTokenRefresh(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap((token) => {
          if (token === 'LOGOUT_IN_PROGRESS') {
            return throwError(() => new Error('Session expired locally, skipping API request.'));
          }
          const retryReq = this.addAuthHeaders(request);
          return next.handle(retryReq);
        })
      );
    }

    // First request to trigger refresh → take the lead
    this.isRefreshing = true;
    this.refreshTokenSubject.next(null); // block queued requests

    return new Observable<HttpEvent<any>>((observer) => {
      this.helper.GetCookieData().then(async (cookieResult: any) => {
        if (!cookieResult || !cookieResult.refreshToken) {
          this.refreshTokenSubject.next('LOGOUT_IN_PROGRESS');
          this.isRefreshing = false;
          if (!this.helper.isLoggingOutNow()) {
            this.notifyService.showError("Session expired! Please sign in again.", "");
          }
          this.helper.Logout(true);
          observer.error(new Error('No refresh token available'));
          return;
        }

        // ── Check if refresh token is already expired mathematically before requesting API ──
        try {
          const payload = cookieResult.refreshToken.split('.')[1];
          const decoded = JSON.parse(atob(payload));
          if (decoded && decoded.exp) {
            const expTime = decoded.exp * 1000;
            if (Date.now() >= expTime) {
              this.refreshTokenSubject.next('LOGOUT_IN_PROGRESS');
              this.isRefreshing = false;
              if (!this.helper.isLoggingOutNow()) {
                this.notifyService.showError("Token is expired. Please sign in again.", "");
              }
              this.helper.Logout(true);
              observer.error(new Error('Refresh token locally expired'));
              return;
            }
          }
        } catch (e) {
          // Ignore parse errors, let backend validate if parsing fails
        }

        // Encrypt the refresh token payload for the API
        const payload = { refreshToken: cookieResult.refreshToken };
        const encryptedData: any = await this.helper.dynamicPush(JSON.stringify(payload));

        this.authService.CreateNewToken({ data: encryptedData }).subscribe({
          next: (response: any) => {

            // ── Refresh token expired (backend returns code 401) ───────────
            if (response && (response.code === 401 || response.code === 403)) {
              this.refreshTokenSubject.next('LOGOUT_IN_PROGRESS');
              this.isRefreshing = false;
              if (!this.helper.isLoggingOutNow()) {
                this.notifyService.showError("Your session has fully expired. Please sign in again.", "");
              }
              this.helper.Logout(true);
              observer.error(new Error('Refresh token expired'));
              return;
            }

            // ── Refresh succeeded → update stored tokens & retry ───────────
            if (response && response.data) {
              const decoded = this.helper.dynamicPop(response.data);
              const newAccessToken: string = decoded.accessToken;

              // Persist updated access token in cookie
              cookieResult.accessToken = newAccessToken;
              const encryptedCookie = CryptoJS.AES.encrypt(
                JSON.stringify(cookieResult),
                this.helper.GetCookieKey()
              ).toString();
              this.cookieService.delete("customer_user", '/');
              this.cookieService.delete("customer_user", '/customer');

              const rememberMeStr = localStorage.getItem("customer_remember_me");
              const isRememberMe = rememberMeStr === 'true' || rememberMeStr === true.toString();

              let expireDays: any = undefined;
              if (isRememberMe && cookieResult.refreshToken) {
                try {
                  const payload = cookieResult.refreshToken.split('.')[1];
                  const decoded = JSON.parse(atob(payload));
                  if (decoded && decoded.exp) {
                    expireDays = new Date(decoded.exp * 1000);
                  }
                } catch (e) {
                  expireDays = 30; // fallback
                }
              }

              this.cookieService.set("customer_user", encryptedCookie, expireDays, '/');

              // Update token in service (localStorage + BehaviorSubject)
              this.authService.setToken(newAccessToken);

              // Unblock all queued requests
              this.refreshTokenSubject.next(newAccessToken);
              this.isRefreshing = false;

              // Retry the original failed request with the fresh token
              const retryReq = this.addAuthHeaders(request);
              next.handle(retryReq).pipe(
                finalize(() => {
                  // Ensure loader is cleared if it wasn't handled by the outer finalize
                  if (!this.shouldSkipLoader(request.url) && this.activeRequests <= 0) {
                    this.activeRequests = 0;
                    this.commonService.updateloadingState(false);
                  }
                })
              ).subscribe({
                next: (event) => observer.next(event),
                error: (err) => observer.error(err),
                complete: () => observer.complete()
              });
            } else {
              // Unexpected empty response
              this.refreshTokenSubject.next('LOGOUT_IN_PROGRESS');
              this.isRefreshing = false;
              if (!this.helper.isLoggingOutNow()) {
                this.notifyService.showError("Session expired! Please sign in again.", "");
              }
              this.helper.Logout(true);
              observer.error(new Error('Token refresh failed: unexpected response'));
            }
          },
          error: (err) => {
            // Network / server error while calling the refresh endpoint
            this.refreshTokenSubject.next('LOGOUT_IN_PROGRESS');
            this.isRefreshing = false;
            if (!this.helper.isLoggingOutNow()) {
              this.notifyService.showError("Session expired! Please sign in again.", "");
            }
            this.helper.Logout(true);
            observer.error(err);
          }
        });

      }).catch((err) => {
        // Cookie read failure
        this.isRefreshing = false;
        this.refreshTokenSubject.next('LOGOUT_IN_PROGRESS');
        this.helper.Logout(true);
        observer.error(err);
      });
    });
  }

  async handleError(httpRequest: HttpRequest<any>, error: any) {
    if (error.status === 401 && !this.isPublicEndpoint(httpRequest.url)) {
      if (!this.helper.isLoggingOutNow()) {
        this.notifyService.showError("Session expired! Please login again.", "");
      }
      this.helper.Logout(true);
      return;
    }

    // For network-level errors (timeout, connection refused, etc.),
    // only show ONE toast no matter how many requests fail simultaneously.
    if (error.status === 0 || error.name === 'TimeoutError') {
      if (!this.networkErrorShown) {
        this.networkErrorShown = true;
        this.notifyService.showError("Something went wrong! Please try again after some time.", "");
      }

      // Reset the flag after a short window so future errors can show again
      clearTimeout(this.networkErrorResetTimer);
      this.networkErrorResetTimer = setTimeout(() => {
        this.networkErrorShown = false;
      }, 3000);
      return;
    }

    if (error.status === 404 || error.status === 500) {
      this.notifyService.showError("Something went wrong!", "");
    } else {
      console.error(error);
    }
  }

  /**
   * URLs that should NOT trigger the global full-screen loader.
   * Use for autocomplete / typeahead style requests where the response is fast
   * enough that the loader would only flicker and be visually distracting.
   */
  private shouldSkipLoader(url: string): boolean {
    return url.includes("api/notification")
      || url.includes("/api/mquotation/v3/mproject/getprojectByGarberFiles/");
  }

  /** Helper to quickly determine if a given JWT token is mathematically expired */
  private isTokenExpired(token: string): boolean {
    if (!token) return true;
    try {
      const payload = token.split('.')[1];
      if (!payload) return false;
      const decoded = JSON.parse(atob(payload));
      if (decoded && decoded.exp) {
        // Leave a 5-second buffer to handle network latency
        return Date.now() >= ((decoded.exp * 1000) - 5000);
      }
      return false;
    } catch (e) {
      return false; // let backend validate if parsing fails
    }
  }

  /**
   * Returns true only when a FormData body contains at least one File or Blob value.
   * Text-only FormData (e.g. form submissions with no attachments selected) returns false
   * so they are treated as regular API calls and never trigger the upload progress UI.
   */
  private hasFileAttachments(body: FormData): boolean {
    for (const value of (body as any).values()) {
      if (value instanceof File || value instanceof Blob) return true;
    }
    return false;
  }

  /**
   * Helper to determine if an endpoint is public and should bypass
   * token validation, refresh flows, and unauthorized errors.
   */
  private isPublicEndpoint(url: string): boolean {
    return url.includes('/forgot-password') ||
      url.includes('/reset-password') ||
      url.includes('/check-url-token') ||
      url.includes('/onboarding-send-otp') ||
      url.includes('/onboarding-check-otp') ||
      url.includes('/client-signup') ||
      url.endsWith('/login') ||
      url.endsWith('/authentication/v1/login');
  }
}
